import { Component, OnInit, OnChanges, SimpleChanges, inject, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { distinctUntilChanged, switchMap, debounceTime } from 'rxjs/operators';
import { of } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { CurrencyMaskDirective } from '../../shared/directives/currency-mask.directive';
import { MaterialModule } from '../../shared/material/material.module';
import { City, CityService } from '../../shared/services/city.service';
import { State, StateService } from '../../shared/services/state.service';
import { data_city_geo } from '../../shared/data/data_city_geo';
import { Profession } from '../../core/generated/model/profession';
import { ProfessionControllerService } from '../../core/generated/api/professionController.service';
import { AvailabilityComponent } from '../../shared/availability/availability.component';
import { SearchServiceControllerService } from '../../core/generated/api/searchServiceController.service';
import { PagedModel } from '../../core/generated/model/pagedModel';
import { PageEvent } from '@angular/material/paginator';
import { AdvancedSearchServiceResultsComponent } from '../advanced-search-service-results/advanced-search-service-results.component';
import { ActiveFiltersCounterComponent } from '../../shared/active-filters-counter/active-filters-counter.component';
import { CityChipsSelectorComponent } from '../../shared/city-chips-selector/city-chips-selector.component';
import { ActiveFilter } from '../models/search-state.models';
import { SearchCacheService } from '../../shared/services/search-cache.service';
import { accordionAnimation, fadeIn, expandCollapse } from '../shared/animations';

interface DataCityGeo {
  ibgeId: number;
  nome: string;
  latitude: number;
  longitude: number;
  mesorregiaoNome: string;
  UFSigla: string;
  UFNome: string;
}

// Enums para billing e council
const BillingUnitEnum: { [key: string]: string } = {
  HOUR: 'Hora',
  SERVICE: 'Atendimento',
  SHIFT: 'Turno'
};

const CouncilGroupEnum: { [key: string]: string } = {
  CRM: 'Conselho Regional De Medicina',
  COREN: 'Conselho Regional De Enfermagem',
  CRO: 'Conselho Regional De Odontologia',
  CRF: 'Conselho Regional De Farmácia',
  CREFITO: 'Conselho Regional De Fisioterapia',
  CRP: 'Conselho Regional De Psicologia',
  CRBM: 'Conselho Regional De Biomedicina',
  CRN: 'Conselho Regional De Nutrição',
  CREF: 'Conselho Regional De Educação Física',
  CREFONO: 'Conselho Regional De Fonoaudiologia',
  CRESS: 'Conselho Regional De Serviço Social',
  CRMV: 'Conselho Regional De Medicina Veterinária',
  CRTR: 'Conselho Regional De Técnicos em Radiologia',
  CRQ: 'Conselho Regional De Química',
  CRBIO: 'Conselho Regional De Biologia'
};

/**
 * AdvancedSearchServiceFormComponent
 * 
 * Formulário de busca avançada para serviços médicos.
 * 
 * Requisitos: 11.2 - Animar expansão/colapso de filtros
 * 
 * @example
 * ```html
 * <app-advanced-search-service-form
 *   [searchConfig]="{ type: 'OFFERING', cpfCnpj: '12345678901' }">
 * </app-advanced-search-service-form>
 * ```
 */
@Component({
  selector: 'app-advanced-search-service-form',
  standalone: true,
  templateUrl: './advanced-search-service-form.component.html',
  styleUrls: ['./advanced-search-service-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MaterialModule,
    CurrencyMaskDirective,
    AvailabilityComponent,
    AdvancedSearchServiceResultsComponent,
    ActiveFiltersCounterComponent,
    CityChipsSelectorComponent
  ],
  animations: [accordionAnimation, fadeIn, expandCollapse]
})
export class AdvancedSearchServiceFormComponent implements OnInit, OnChanges {
  @Input() searchConfig: { type?: 'OFFERING' | 'DEMAND'; cpfCnpj?: string } | null = null;

  // Services
  private readonly professionsService = inject(ProfessionControllerService);
  private readonly searchServiceControllerService = inject(SearchServiceControllerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly cityService = inject(CityService);
  private readonly stateService = inject(StateService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly searchCacheService = inject(SearchCacheService);

  // Form and data
  searchForm!: FormGroup;
  states: State[] = [];
  cities: DataCityGeo[] = [];
  nearbyCities: City[] = [];
  selectedCities: City[] = [];
  professionsOptions: Profession[] = [];

  // UI state
  isLoading = false;
  isMobile = false;
  showActivityAreas = false;
  private _editingMode = false;
  isFromCache = false; // Indica se os resultados vieram do cache

  // Accordion state for mobile
  expandedSections = {
    professional: true,
    location: false,
    details: false
  };

  // Pagination
  pageSize = 10;
  pageIndex = 0;
  resultTest: PagedModel | null = null;

  // Active filters
  activeFilters: ActiveFilter[] = [];

  // Options
  readonly billingUnitEnum = BillingUnitEnum;
  readonly councilGroupEnum = CouncilGroupEnum;

  get editingMode(): boolean {
    return this._editingMode;
  }

  set editingMode(value: boolean) {
    this._editingMode = value;
    if (value) {
      this.updateShowActivityAreas();
    }
  }

  get activityAreas(): string[] {
    return this.searchForm.get('activityAreas')?.value || [];
  }

  get availability(): FormArray {
    return this.searchForm.get('availability') as FormArray;
  }

  get emergencyServiceAvailability(): FormArray {
    return this.searchForm.get('emergencyServiceAvailability') as FormArray;
  }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();
    this.initForm();
    this.setupFormSubscriptions();
    this.loadStates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchConfig']?.currentValue && this.searchForm) {
      const cfg = changes['searchConfig'].currentValue;
      if (cfg?.cnpj) {
        this.searchForm.get('searcherCpfCnpj')?.setValue(cfg.cnpj);
      }
    }
  }

  private initForm(): void {
    this.searchForm = this.fb.group({
      searcherCpfCnpj: [''],
      councilGroup: ['', Validators.required],
      professionName: ['', [Validators.maxLength(100)]],
      specialty: ['', [Validators.maxLength(100)]],
      activityAreas: [[]],
      adultCare: [null],
      pediatricCare: [null],
      serviceFormatOnLine: [null],
      serviceFormatPresential: [null],
      state: [''],
      referenceCity: [{ value: '', disabled: true }],
      radiusInKm: [0, [Validators.min(0), Validators.max(500)]],
      nearbyCities: [this.selectedCities],
      amountMin: [null, [Validators.min(0)]],
      amountMax: [null, [Validators.min(0)]],
      billingUnit: [''],
      availability: this.fb.array(this.getAllSlots()),
      emergencyServiceAvailability: this.fb.array(this.getAllSlots()),
    }, { validators: this.validateAmountRange });
  }

  /**
   * Custom validator for amount range
   */
  private validateAmountRange(group: FormGroup): ValidationErrors | null {
    const min = group.get('amountMin')?.value ?? null;
    const max = group.get('amountMax')?.value ?? null;

    if (min !== null && max !== null && min > max) {
      return { amountRangeInvalid: true };
    }
    return null;
  }

  /**
   * Get error message for a form control
   */
  getErrorMessage(controlName: string): string {
    const control = this.searchForm.get(controlName);
    if (!control || !control.errors) return '';

    if (control.hasError('required')) {
      return 'Este campo é obrigatório';
    }
    if (control.hasError('maxLength')) {
      const maxLength = control.errors['maxLength']?.requiredLength;
      return `Máximo de ${maxLength} caracteres`;
    }
    if (control.hasError('min')) {
      const min = control.errors['min']?.min;
      return `Valor mínimo: ${min}`;
    }
    if (control.hasError('max')) {
      const max = control.errors['max']?.max;
      return `Valor máximo: ${max}`;
    }
    return '';
  }

  /**
   * Get error message for amount range validation
   */
  getAmountRangeError(): string {
    if (this.searchForm.hasError('amountRangeInvalid')) {
      return 'O preço mínimo não pode ser maior que o máximo';
    }
    return '';
  }

  /**
   * Check if a control has been touched and is invalid
   */
  showError(controlName: string): boolean {
    const control = this.searchForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Check if a control is valid and has value
   */
  showSuccess(controlName: string): boolean {
    const control = this.searchForm.get(controlName);
    return !!(control && control.valid && control.value && (control.dirty || control.touched));
  }

  private setupFormSubscriptions(): void {
    // Compute active filters on any form change
    this.searchForm.valueChanges.subscribe(() => {
      this.computeActiveFilters();
    });

    // Profession and specialty changes with debounce
    this.searchForm.get('professionName')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => this.updateShowActivityAreas());

    this.searchForm.get('specialty')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => this.updateShowActivityAreas());

    // Council group changes
    this.searchForm.get('councilGroup')?.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe(councilGroupValue => {
      if (councilGroupValue) {
        this.getProfessions();
      } else {
        this.professionsOptions = [];
      }
      this.searchForm.patchValue({
        professionName: '',
        specialty: ''
      });
      this.resetActivityAreas();
    });

    // State changes
    this.searchForm.get('state')?.valueChanges.pipe(
      distinctUntilChanged(),
      switchMap(stateCode => {
        this.searchForm.get('referenceCity')?.setValue('');
        this.cities = [];

        if (stateCode) {
          // Verifica cache de cidades por estado
          const citiesCacheKey = `cities_${stateCode}`;
          const cachedCities = this.searchCacheService.get<DataCityGeo[]>(citiesCacheKey);
          
          if (cachedCities) {
            return of(cachedCities);
          }
          
          this.isLoading = true;
          const cities = data_city_geo.filter(city => city.UFSigla === stateCode) as DataCityGeo[];
          
          // Armazena no cache (TTL de 1 hora para cidades)
          this.searchCacheService.set(citiesCacheKey, cities, 60 * 60 * 1000);
          
          return of(cities);
        }
        return of([]);
      })
    ).subscribe(cities => {
      this.cities = cities;
      this.isLoading = false;

      const referenceCityControl = this.searchForm.get('referenceCity');
      if (cities?.length > 0) {
        referenceCityControl?.enable({ emitEvent: false });
      } else {
        referenceCityControl?.disable({ emitEvent: false });
        referenceCityControl?.setValue('');
      }
    });

    // City and radius changes with debounce
    this.searchForm.get('referenceCity')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      if (this.searchForm.get('radiusInKm')?.value > 0) {
        this.updateNearbyCities();
      }
    });

    this.searchForm.get('radiusInKm')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.updateNearbyCities();
    });

    // Amount fields validation with debounce
    this.searchForm.get('amountMin')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.searchForm.updateValueAndValidity({ emitEvent: false });
    });

    this.searchForm.get('amountMax')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.searchForm.updateValueAndValidity({ emitEvent: false });
    });
  }

  private loadStates(): void {
    this.stateService.getStates().subscribe(states => {
      this.states = states;
    });
  }

  private getAllSlots(): number[] {
    const slots: number[] = [];
    for (let dayId = 2; dayId <= 8; dayId++) {
      for (let periodId = 1; periodId <= 3; periodId++) {
        slots.push(parseInt(`${dayId}${periodId}`));
      }
    }
    return slots;
  }

  getProfessions(): void {
    this.professionsService.findAll3().subscribe((items: Profession[]) => {
      this.professionsOptions = items.filter(
        (item: Profession) => item.active && item.councilGroup === this.searchForm.get('councilGroup')?.value
      );
      this.updateShowActivityAreas();
    });
  }

  getProfessionName(): string[] {
    if (!this.professionsOptions) return [];
    return [...new Set(this.professionsOptions.map(item => item.professionName).filter((name): name is string => name !== undefined))];
  }

  getSpecialty(): string[] {
    const professionName = this.searchForm.get('professionName')?.value;
    if (!professionName || !this.professionsOptions) return [];

    const filtered = this.professionsOptions.filter(
      cp => cp.professionName === professionName
    );
    return [...new Set(filtered.map(item => item.specialty).filter((specialty): specialty is string => specialty !== undefined))];
  }

  getActivityArea(): string[] {
    const professionName = this.searchForm.get('professionName')?.value;
    const specialty = this.searchForm.get('specialty')?.value;

    if (!specialty || !this.professionsOptions) return [];

    const filtered = this.professionsOptions.filter(
      cp => cp.professionName === professionName && cp.specialty === specialty
    );
    return [...new Set(filtered.map(item => item.activityArea).filter((area): area is string => area !== undefined))];
  }

  resetActivityAreas(): void {
    this.searchForm.get('activityAreas')?.setValue([]);
    this.showActivityAreas = false;
  }

  onAreaCheckboxChange(event: any, area: string): void {
    const currentAreas = this.activityAreas;
    if (event.checked) {
      if (!currentAreas.includes(area)) {
        this.searchForm.get('activityAreas')?.setValue([...currentAreas, area]);
      }
    } else {
      this.searchForm.get('activityAreas')?.setValue(currentAreas.filter(a => a !== area));
    }
  }

  isAreaSelected(area: string): boolean {
    return this.activityAreas.includes(area);
  }

  updateNearbyCities(): void {
    const selectedCity = this.searchForm.get('referenceCity')?.value;
    const radiusInKm = this.searchForm.get('radiusInKm')?.value;

    if (!selectedCity || radiusInKm <= 0) {
      this.nearbyCities = [];
      this.selectedCities = [];
      this.searchForm.get('nearbyCities')?.setValue([]);
      return;
    }

    const cityObj = this.cities.find(city => city.nome === selectedCity);
    if (cityObj) {
      this.isLoading = true;
      this.nearbyCities = this.cityService.findCitiesInRadius(cityObj, radiusInKm);

      this.selectedCities = this.selectedCities.filter(selected =>
        this.nearbyCities.some(nearby =>
          nearby.name === selected.name && nearby.state === selected.state
        )
      );

      this.searchForm.get('nearbyCities')?.setValue([...this.selectedCities]);
      this.isLoading = false;
    }
  }

  /**
   * Manipula mudanças na seleção de cidades do CityChipsSelectorComponent
   */
  onCitySelectionChange(cities: City[]): void {
    this.selectedCities = cities;
    this.searchForm.get('nearbyCities')?.setValue([...cities]);
  }

  submitSearch(): void {
    this.editingMode = false;

    if (!this.searchForm.valid || !this.canSearch()) return;

    const formValue = this.searchForm.value;
    const cities = this.selectedCities.map(city => city.name);

    if (formValue.referenceCity) {
      cities.push(formValue.referenceCity);
    }

    const searchRequest: any = {
      councilGroup: formValue.councilGroup,
      professionName: formValue.professionName,
      specialty: formValue.specialty,
      activityAreas: formValue.activityAreas,
      state: formValue.state,
      referenceCity: cities.length ? cities : undefined,
      amountMin: formValue.amountMin,
      amountMax: formValue.amountMax,
      billingUnit: formValue.billingUnit,
      availability: formValue.availability,
      emergencyServiceAvailability: formValue.emergencyServiceAvailability
    };

    // Adicionar apenas se forem true
    if (formValue.adultCare === true) {
      searchRequest.adultCare = true;
    }
    if (formValue.pediatricCare === true) {
      searchRequest.pediatricCare = true;
    }
    if (formValue.serviceFormatOnLine === true) {
      searchRequest.serviceFormatOnLine = true;
    }
    if (formValue.serviceFormatPresential === true) {
      searchRequest.serviceFormatPresential = true;
    }

    // Remove empty values
    Object.keys(searchRequest).forEach(key => {
      if (searchRequest[key] === null || searchRequest[key] === '') {
        delete searchRequest[key];
      }
    });

    this.pageIndex = 0;
    this.loadSearch(this.pageIndex, this.pageSize, searchRequest);
  }

  loadSearch(pageIndex: number = 0, pageSize: number = 10, overrideRequest?: any, bypassCache: boolean = false): void {
    const formValue = this.searchForm.value;
    const cities = this.selectedCities.map(city => city.name);

    const requestParams = overrideRequest || {
      councilGroup: formValue.councilGroup,
      professionName: formValue.professionName,
      specialty: formValue.specialty,
      activityAreas: formValue.activityAreas,
      adultCare: formValue.adultCare || null,
      pediatricCare: formValue.pediatricCare || null,
      serviceFormatOnLine: formValue.serviceFormatOnLine || null,
      serviceFormatPresential: formValue.serviceFormatPresential || null,
      state: formValue.state,
      referenceCity: cities.length
        ? [...cities, formValue.referenceCity].filter(Boolean)
        : (formValue.referenceCity ? [formValue.referenceCity] : undefined),
      amountMin: formValue.amountMin,
      amountMax: formValue.amountMax,
      billingUnit: formValue.billingUnit,
      availability: formValue.availability,
      emergencyServiceAvailability: formValue.emergencyServiceAvailability,
      page: pageIndex,
      size: pageSize
    };

    // Gera chave de cache baseada nos parâmetros de busca
    const cacheKey = this.searchCacheService.generateKey({
      ...requestParams,
      searchType: this.searchConfig?.type
    });

    // Verifica cache se não for bypass
    if (!bypassCache) {
      const cachedResult = this.searchCacheService.get<PagedModel>(cacheKey);
      if (cachedResult) {
        this.resultTest = cachedResult;
        this.isFromCache = true;
        this.cdr.markForCheck();
        return;
      }
    }

    this.isLoading = true;
    this.isFromCache = false;

    const searchObservable = this.searchConfig?.type === 'DEMAND'
      ? this.searchServiceControllerService.searchDemand(requestParams)
      : this.searchServiceControllerService.searchOffering(requestParams);

    searchObservable.subscribe({
      next: (response: PagedModel | null) => {
        this.resultTest = response as PagedModel;
        
        // Armazena no cache
        if (response) {
          this.searchCacheService.set(cacheKey, response);
        }
        
        this.cdr.markForCheck();
      },
      error: () => {
        this.resultTest = null;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadSearch(this.pageIndex, this.pageSize);
  }

  /**
   * Atualiza os resultados ignorando o cache
   */
  refreshSearch(): void {
    this.loadSearch(this.pageIndex, this.pageSize, undefined, true);
  }

  async resetForm(): Promise<void> {
    this.searchForm.reset({
      radiusInKm: 0,
      adultCare: false,
      pediatricCare: false,
      serviceFormatOnLine: false,
      serviceFormatPresential: false,
      activityAreas: [],
      nearbyCities: []
    });

    this.searchForm.setControl('availability', this.fb.array(this.getAllSlots()));
    this.searchForm.setControl('emergencyServiceAvailability', this.fb.array(this.getAllSlots()));

    this.selectedCities = [];
    this.nearbyCities = [];
    this.cities = [];
  }

  newSearch(): void {
    this.resultTest = null;
    this.initForm();
    this.selectedCities = [];
    this.nearbyCities = [];
    this.cities = [];
    this.editingMode = false;
    this.cdr.markForCheck();
  }

  editSearch(): void {
    this.editingMode = true;
    this.updateShowActivityAreas();
    this.cdr.markForCheck();
  }

  canSearch = (): boolean => {
    const formValues = this.searchForm.value;
    return Object.values(formValues).some(value => {
      if (typeof value === 'boolean') return value === true;
      return value !== null && value !== '' && value !== 0;
    }) || this.selectedCities.length > 0;
  }

  onAvailabilityChange(newAvailability: FormArray): void {
    this.searchForm.setControl('availability', newAvailability);
  }

  onEmergencyAvailabilityChange(newAvailability: FormArray): void {
    this.searchForm.setControl('emergencyServiceAvailability', newAvailability);
  }

  onSpecialtyChange(): void {
    this.showActivityAreas = false;
    setTimeout(() => {
      this.searchForm.get('activityAreas')?.setValue([]);
      this.updateShowActivityAreas();
      this.cdr.markForCheck();
    }, 300);
  }

  private updateShowActivityAreas(): void {
    try {
      const count = this.getActivityArea()?.length || 0;
      this.showActivityAreas = count > 0;
    } catch {
      this.showActivityAreas = false;
    }
    this.cdr.markForCheck();
  }

  /**
   * Computa os filtros ativos do formulário
   */
  computeActiveFilters(): void {
    const filters: ActiveFilter[] = [];
    const formValue = this.searchForm.value;

    // Informações Profissionais
    if (formValue.councilGroup) {
      const councilLabel = this.councilGroupEnum[formValue.councilGroup as keyof typeof this.councilGroupEnum];
      filters.push({
        category: 'Profissional',
        label: 'Conselho',
        value: `${formValue.councilGroup} - ${councilLabel}`,
        fieldName: 'councilGroup'
      });
    }

    if (formValue.professionName) {
      filters.push({
        category: 'Profissional',
        label: 'Profissão',
        value: formValue.professionName,
        fieldName: 'professionName'
      });
    }

    if (formValue.specialty) {
      filters.push({
        category: 'Profissional',
        label: 'Especialidade',
        value: formValue.specialty,
        fieldName: 'specialty'
      });
    }

    if (formValue.activityAreas?.length > 0) {
      filters.push({
        category: 'Profissional',
        label: 'Áreas de Atuação',
        value: `${formValue.activityAreas.length} selecionada(s)`,
        fieldName: 'activityAreas'
      });
    }

    if (formValue.adultCare) {
      filters.push({
        category: 'Profissional',
        label: 'Atendimento',
        value: 'Adulto',
        fieldName: 'adultCare'
      });
    }

    if (formValue.pediatricCare) {
      filters.push({
        category: 'Profissional',
        label: 'Atendimento',
        value: 'Pediátrico',
        fieldName: 'pediatricCare'
      });
    }

    if (formValue.serviceFormatOnLine) {
      filters.push({
        category: 'Profissional',
        label: 'Formato',
        value: 'Online',
        fieldName: 'serviceFormatOnLine'
      });
    }

    if (formValue.serviceFormatPresential) {
      filters.push({
        category: 'Profissional',
        label: 'Formato',
        value: 'Presencial',
        fieldName: 'serviceFormatPresential'
      });
    }

    // Localização
    if (formValue.state) {
      const stateName = this.states.find(s => s.code === formValue.state)?.name || formValue.state;
      filters.push({
        category: 'Localização',
        label: 'Estado',
        value: stateName,
        fieldName: 'state'
      });
    }

    if (formValue.referenceCity) {
      filters.push({
        category: 'Localização',
        label: 'Cidade de Referência',
        value: formValue.referenceCity,
        fieldName: 'referenceCity'
      });
    }

    if (formValue.radiusInKm > 0) {
      filters.push({
        category: 'Localização',
        label: 'Raio',
        value: `${formValue.radiusInKm} km`,
        fieldName: 'radiusInKm'
      });
    }

    if (this.selectedCities.length > 0) {
      filters.push({
        category: 'Localização',
        label: 'Cidades Selecionadas',
        value: `${this.selectedCities.length} cidade(s)`,
        fieldName: 'nearbyCities'
      });
    }

    // Detalhes do Serviço
    if (formValue.amountMin !== null && formValue.amountMin > 0) {
      filters.push({
        category: 'Detalhes',
        label: 'Preço Mínimo',
        value: `R$ ${formValue.amountMin.toFixed(2)}`,
        fieldName: 'amountMin'
      });
    }

    if (formValue.amountMax !== null && formValue.amountMax > 0) {
      filters.push({
        category: 'Detalhes',
        label: 'Preço Máximo',
        value: `R$ ${formValue.amountMax.toFixed(2)}`,
        fieldName: 'amountMax'
      });
    }

    if (formValue.billingUnit) {
      const unitLabel = this.billingUnitEnum[formValue.billingUnit as keyof typeof this.billingUnitEnum];
      filters.push({
        category: 'Detalhes',
        label: 'Unidade de Cobrança',
        value: unitLabel,
        fieldName: 'billingUnit'
      });
    }

    // Disponibilidade
    const availabilityCount = formValue.availability?.filter((v: number) => v).length || 0;
    if (availabilityCount > 0 && availabilityCount < 21) {
      filters.push({
        category: 'Detalhes',
        label: 'Disponibilidade',
        value: `${availabilityCount} período(s)`,
        fieldName: 'availability'
      });
    }

    const emergencyCount = formValue.emergencyServiceAvailability?.filter((v: number) => v).length || 0;
    if (emergencyCount > 0 && emergencyCount < 21) {
      filters.push({
        category: 'Detalhes',
        label: 'Disponibilidade Plantonista',
        value: `${emergencyCount} período(s)`,
        fieldName: 'emergencyServiceAvailability'
      });
    }

    this.activeFilters = filters;
  }

  /**
   * Remove um filtro específico
   */
  onFilterRemoved(filter: ActiveFilter): void {
    const control = this.searchForm.get(filter.fieldName);
    
    if (control) {
      // Handle different field types
      if (filter.fieldName === 'activityAreas') {
        control.setValue([]);
      } else if (filter.fieldName === 'nearbyCities') {
        this.selectedCities = [];
        control.setValue([]);
      } else if (typeof control.value === 'boolean') {
        control.setValue(false);
      } else if (typeof control.value === 'number') {
        control.setValue(filter.fieldName === 'radiusInKm' ? 0 : null);
      } else {
        control.setValue('');
      }
    }

    this.computeActiveFilters();
  }

  /**
   * Navega para o campo do filtro
   */
  onFilterClicked(fieldName: string): void {
    const element = document.querySelector(`[formControlName="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Focus the element if it's an input
      if (element instanceof HTMLElement) {
        setTimeout(() => element.focus(), 300);
      }
    }
  }

  /**
   * Limpa todos os filtros
   */
  onClearAllFilters(): void {
    this.resetForm();
    this.computeActiveFilters();
  }

  /**
   * Alterna a expansão de uma seção do accordion (mobile)
   */
  toggleSection(section: 'professional' | 'location' | 'details'): void {
    if (this.isMobile) {
      this.expandedSections[section] = !this.expandedSections[section];
    }
  }

  /**
   * Verifica se uma seção está expandida
   */
  isSectionExpanded(section: 'professional' | 'location' | 'details'): boolean {
    return !this.isMobile || this.expandedSections[section];
  }

}


